import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, User, LogIn, UserPlus, Clock, CheckCircle2, AlertCircle, AlertTriangle, Zap, CreditCard, Wallet, Gift, Tag, Globe, MapPin, MessageCircle, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PaymentModal from './PaymentModal';
import ReCAPTCHA from 'react-google-recaptcha';

const WHATSAPP_LINK = "https://wa.me/message/3PBT5TZW3SARB1";

// Country list with flags
const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'HT', name: 'Haiti', flag: '🇭🇹' },
  { code: 'JM', name: 'Jamaica', flag: '🇯🇲' },
  { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹' },
  { code: 'BB', name: 'Barbados', flag: '🇧🇧' },
  { code: 'GY', name: 'Guyana', flag: '🇬🇾' },
  { code: 'BS', name: 'Bahamas', flag: '🇧🇸' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼' },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷' },
  { code: 'RO', name: 'Romania', flag: '🇷🇴' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'PE', name: 'Peru', flag: '🇵🇪' },
  { code: 'VE', name: 'Venezuela', flag: '🇻🇪' },
  { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴' },
  { code: 'PR', name: 'Puerto Rico', flag: '🇵🇷' },
  { code: 'CU', name: 'Cuba', flag: '🇨🇺' },
  { code: 'OTHER', name: 'Other', flag: '🌍' }
].sort((a, b) => a.name.localeCompare(b.name));

export const getCountryFlag = (code) => {
  const country = COUNTRIES.find(c => c.code === code);
  return country ? country.flag : '🌍';
};

export const getCountryName = (code) => {
  const country = COUNTRIES.find(c => c.code === code);
  return country ? country.name : code;
};

const GuestAuthModal = ({ isOpen, onClose, onSuccess }) => {
  const [mode, setMode] = useState('login'); // 'login', 'login_password', 'signup', 'verify_email', 'choose_plan', 'complete_profile', 'set_password', 'forgot_password', 'reset_password', 'waiting_review'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupData, setSignupData] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingPaymentEmail, setPendingPaymentEmail] = useState('');
  const [profileUser, setProfileUser] = useState(null);
  
  // Login flow state
  const [loginEmail, setLoginEmail] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [resetCode, setResetCode] = useState('');
  
  // Mailing address state
  const [mailingStreet, setMailingStreet] = useState('');
  const [mailingCity, setMailingCity] = useState('');
  const [mailingState, setMailingState] = useState('');
  const [mailingZip, setMailingZip] = useState('');
  
  // Billing address state
  const [billingStreet, setBillingStreet] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [billingCountry, setBillingCountry] = useState('');
  const [billingPhone, setBillingPhone] = useState('');
  const [billingSameAsMailing, setBillingSameAsMailing] = useState(false);
  // Email verification state
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendingCode, setResendingCode] = useState(false);
  
  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  
  // reCAPTCHA state
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const recaptchaRef = useRef(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

  // Always use tradepayflow.emergent.host for bonbot.biz custom domain
  const getApiBase = () => {
    const hostname = window.location.hostname;
    if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
      return 'https://bonbot.biz';
    }
    return process.env.REACT_APP_BACKEND_URL || window.location.origin;
  };
  const API = getApiBase();

  // Check for referral code in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      setMode('signup');
    }
  }, [isOpen]);

  const validateReferralCode = async (code) => {
    if (!code.trim()) {
      setReferralInfo(null);
      return;
    }
    
    setValidatingCode(true);
    try {
      const response = await fetch(`${API}/api/referral/validate?code=${code.toUpperCase()}`);
      const data = await response.json();
      
      if (data.valid) {
        setReferralInfo(data);
      } else {
        setReferralInfo({ valid: false, message: data.message || 'Invalid code' });
      }
    } catch (err) {
      setReferralInfo({ valid: false, message: 'Error validating code' });
    } finally {
      setValidatingCode(false);
    }
  };

  const handleSignup = async () => {
    setError('');
    setStatusMessage(null);
    setLoading(true);

    // Validate country
    if (!country) {
      setError('Please select your country');
      setLoading(false);
      return;
    }
    
    // Validate mailing address
    if (!mailingStreet || !mailingCity || !mailingState || !mailingZip) {
      setError('Please fill in your complete mailing address');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/api/guest/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          name: name || undefined,
          country: country,
          referral_code: referralInfo?.valid ? referralCode.toUpperCase() : referralCode || null,
          mailing_street: mailingStreet,
          mailing_city: mailingCity,
          mailing_state: mailingState,
          mailing_zip: mailingZip
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.requires_verification) {
          // Need to verify email first
          setVerificationEmail(email);
          setMode('verify_email');
          setStatusMessage({
            type: 'success',
            text: data.message,
            icon: <Mail className="w-5 h-5" />
          });
          // For testing - show debug code if available
          if (data.debug_code) {
            console.log('Debug verification code:', data.debug_code);
          }
        } else {
          // Old flow - show payment modal
          setSignupData({ ...data, referralInfo: referralInfo?.valid ? referralInfo : null });
          setSignupSuccess(true);
          setPendingPaymentEmail(email);
          if (data.show_payment_modal || data.requires_payment) {
            setShowPaymentModal(true);
          }
        }
      } else {
        setError(data.message || 'Something went wrong');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerifyEmail = async () => {
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/guest/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: verificationEmail,
          code: verificationCode
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Email verified - now show payment modal
        setPendingPaymentEmail(verificationEmail);
        setShowPaymentModal(true);
        setMode('login'); // Reset mode
      } else {
        setError(data.message || 'Invalid verification code');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendCode = async () => {
    setResendingCode(true);
    setError('');
    
    try {
      const response = await fetch(`${API}/api/guest/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStatusMessage({
          type: 'success',
          text: 'New verification code sent!',
          icon: <CheckCircle2 className="w-5 h-5" />
        });
        if (data.debug_code) {
          console.log('Debug verification code:', data.debug_code);
        }
      } else {
        setError(data.message || 'Failed to resend code');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setResendingCode(false);
    }
  };
  
  const handleSetPassword = async () => {
    setError('');
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/guest/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pendingPaymentEmail,
          password: password
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Password set - show waiting for review
        setMode('waiting_review');
      } else {
        setError(data.message || 'Failed to set password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Check email and determine if user has password
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setError('');
    setStatusMessage(null);
    setLoading(true);

    try {
      const response = await fetch(`${API}/api/guest/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!data.exists) {
        setError(data.message || 'Email not found. Please sign up first.');
      } else if (data.requires_verification) {
        // Need to verify email first
        setVerificationEmail(email);
        setMode('verify_email');
      } else if (data.has_password) {
        // User has password - show password field
        setLoginEmail(email);
        setHasPassword(true);
        setMode('login_password');
      } else {
        // User doesn't have password - prompt to create one
        setLoginEmail(email);
        setPendingPaymentEmail(email);
        setHasPassword(false);
        setMode('set_password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Step 2: Login with password
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setCaptchaError('');
    setStatusMessage(null);
    
    // Verify captcha
    if (!captchaToken) {
      setCaptchaError('Please verify you are human');
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API}/api/guest/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: loginEmail, 
          password,
          captcha_token: captchaToken
        })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('guestToken', data.token);
        localStorage.setItem('guestUser', JSON.stringify(data.user));
        
        // Also store subscription_expired flag if present
        if (data.subscription_expired || data.show_renewal_modal) {
          localStorage.setItem('show_renewal_modal', 'true');
        }
        
        // Reset captcha on success
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
        
        // Check if profile needs update (existing users without mailing address)
        if (data.needs_profile_update) {
          setProfileUser(data.user);
          setMode('complete_profile');
        } else {
          onSuccess(data.user, data.subscription_expired);
          onClose();
        }
      } else {
        // Reset captcha on failure
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
        
        // Handle different statuses
        if (data.status === 'pending_email_verification' || data.requires_verification) {
          setVerificationEmail(loginEmail);
          setMode('verify_email');
          setStatusMessage({
            type: 'warning',
            text: data.message,
            icon: <Mail className="w-5 h-5" />
          });
        } else if (data.requires_password_setup) {
          setPendingPaymentEmail(loginEmail);
          setMode('set_password');
        } else if (data.status === 'pending_payment') {
          setPendingPaymentEmail(data.user_email || loginEmail);
          if (data.show_payment_modal) {
            setShowPaymentModal(true);
          } else {
            setStatusMessage({
              type: 'warning',
              text: data.message,
              icon: <CreditCard className="w-5 h-5" />
            });
          }
        } else if (data.status === 'pending_approval') {
          setPendingPaymentEmail(loginEmail);
          setMode('waiting_review');
        } else if (data.status === 'trial_expired' || data.status === 'subscription_expired' || data.requires_renewal) {
          // Subscription/Trial expired - show renewal modal with redirect option
          setStatusMessage({
            type: 'error',
            icon: <AlertCircle className="w-5 h-5" />,
            title: 'Subscription Expired',
            message: data.message,
            showRenewal: true,
            renewalInfo: data.renewal_info,
            redirectUrl: data.redirect_url
          });
          setPendingPaymentEmail(loginEmail);
        } else if (data.status === 'blocked' || data.status === 'paused') {
          setStatusMessage({
            type: 'error',
            icon: <AlertCircle className="w-5 h-5" />,
            title: 'Access Paused',
            message: data.message,
            showPayment: data.status === 'paused'
          });
          if (data.status === 'paused') {
            setPendingPaymentEmail(loginEmail);
          }
        } else {
          setError(data.message || 'Login failed');
        }
      }
    } catch (err) {
      // Reset captcha on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaToken(null);
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteProfile = async () => {
    // Validate mailing address
    if (!mailingStreet || !mailingCity || !mailingState || !mailingZip || !country) {
      setError('Please fill in all mailing address fields');
      return;
    }
    
    // Validate billing address
    if (!billingSameAsMailing) {
      if (!billingStreet || !billingCity || !billingState || !billingZip || !billingCountry || !billingPhone) {
        setError('Please fill in all billing address fields including phone number');
        return;
      }
    } else if (!billingPhone) {
      setError('Please enter your phone number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('guestToken');
      const userEmail = profileUser?.email || loginEmail;
      
      // Update mailing address
      const profileData = {
        country,
        mailing_street: mailingStreet,
        mailing_city: mailingCity,
        mailing_state: mailingState,
        mailing_zip: mailingZip,
        mailing_country: country
      };
      
      await fetch(`${API}/api/guest/update-profile?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });
      
      // Update billing address
      const billingData = {
        email: userEmail,
        billing_street: billingSameAsMailing ? mailingStreet : billingStreet,
        billing_city: billingSameAsMailing ? mailingCity : billingCity,
        billing_state: billingSameAsMailing ? mailingState : billingState,
        billing_zip: billingSameAsMailing ? mailingZip : billingZip,
        billing_country: billingSameAsMailing ? country : billingCountry,
        billing_phone: billingPhone,
        same_as_mailing: billingSameAsMailing
      };
      
      const billingResponse = await fetch(`${API}/api/guest/update-billing-address`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(billingData)
      });
      
      const billingResult = await billingResponse.json();
      
      if (billingResult.success) {
        // Update user data
        const updatedUser = { 
          ...profileUser, 
          country, 
          profile_complete: true,
          mailing_address: {
            street: mailingStreet,
            city: mailingCity,
            state: mailingState,
            zip_code: mailingZip,
            country: country
          },
          billing_address: {
            street: billingSameAsMailing ? mailingStreet : billingStreet,
            city: billingSameAsMailing ? mailingCity : billingCity,
            state: billingSameAsMailing ? mailingState : billingState,
            zip_code: billingSameAsMailing ? mailingZip : billingZip,
            country: billingSameAsMailing ? country : billingCountry,
            phone: billingPhone
          }
        };
        localStorage.setItem('guestUser', JSON.stringify(updatedUser));
        onSuccess(updatedUser);
        onClose();
      } else {
        setError(billingResult.message || 'Failed to update profile');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Forgot password handler
  const handleForgotPassword = async () => {
    setError('');
    setLoading(true);
    
    const emailToReset = loginEmail || email;
    if (!emailToReset) {
      setError('Please enter your email first');
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/guest/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailToReset })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setLoginEmail(emailToReset);
        setMode('reset_password');
        setStatusMessage({
          type: 'success',
          text: data.message,
          icon: <Mail className="w-5 h-5" />
        });
        if (data.debug_code) {
          console.log('Debug reset code:', data.debug_code);
        }
      } else {
        setError(data.message || 'Failed to send reset code');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Reset password with code
  const handleResetPassword = async () => {
    setError('');
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/guest/reset-password?email=${encodeURIComponent(loginEmail)}&code=${resetCode}&new_password=${encodeURIComponent(password)}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStatusMessage({
          type: 'success',
          text: 'Password reset successfully! You can now login.',
          icon: <CheckCircle2 className="w-5 h-5" />
        });
        setMode('login_password');
        setPassword('');
        setConfirmPassword('');
        setResetCode('');
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToApp = () => {
    if (signupData?.token && signupData?.user) {
      onSuccess(signupData.user);
      onClose();
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setCountry('');
    setError('');
    setStatusMessage(null);
    setSignupSuccess(false);
    setSignupData(null);
    setReferralCode('');
    setReferralInfo(null);
    setProfileUser(null);
    setMailingStreet('');
    setMailingCity('');
    setMailingState('');
    setMailingZip('');
    setBillingStreet('');
    setBillingCity('');
    setBillingState('');
    setBillingZip('');
    setBillingCountry('');
    setBillingPhone('');
    setBillingSameAsMailing(false);
    setVerificationCode('');
    setVerificationEmail('');
    setLoginEmail('');
    setHasPassword(false);
    setResetCode('');
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="bg-slate-900 border border-white/20 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="relative p-6 pb-4 border-b border-white/10 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
            <button
              onClick={() => { resetForm(); onClose(); }}
              className="absolute right-4 top-4 p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                {mode === 'login' ? <LogIn className="w-6 h-6 text-white" /> : 
                 mode === 'complete_profile' ? <MapPin className="w-6 h-6 text-white" /> :
                 <UserPlus className="w-6 h-6 text-white" />}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {mode === 'login' ? 'Welcome Back' : 
                   mode === 'complete_profile' ? 'Complete Your Profile' :
                   mode === 'choose_plan' ? 'Choose Your Plan' : 'Create Account'}
                </h2>
                <p className="text-sm text-gray-400">
                  {mode === 'login' ? 'Sign in to access signals' : 
                   mode === 'complete_profile' ? 'Tell us where you\'re from' :
                   mode === 'choose_plan' ? 'Select the best option for you' : 'Join our trading community'}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Complete Profile Mode */}
            {mode === 'complete_profile' && (
              <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="text-center mb-4">
                  <MapPin className="w-12 h-12 mx-auto mb-2 text-blue-400" />
                  <p className="text-gray-300">Complete your profile with address information</p>
                </div>
                
                {/* Country Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Country *
                  </label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger className="w-full bg-white/10 border-white/20 text-white">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-[300px]">
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code} className="text-white hover:bg-white/10">
                          <span className="flex items-center gap-2">
                            <span>{c.flag}</span>
                            <span>{c.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Mailing Address Section */}
                <div className="pt-2 border-t border-white/10">
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Mailing Address *
                  </label>
                  <div className="space-y-3">
                    <Input
                      type="text"
                      value={mailingStreet}
                      onChange={(e) => setMailingStreet(e.target.value)}
                      placeholder="Street Address"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        type="text"
                        value={mailingCity}
                        onChange={(e) => setMailingCity(e.target.value)}
                        placeholder="City"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                      <Input
                        type="text"
                        value={mailingState}
                        onChange={(e) => setMailingState(e.target.value)}
                        placeholder="State/Province"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <Input
                      type="text"
                      value={mailingZip}
                      onChange={(e) => setMailingZip(e.target.value)}
                      placeholder="ZIP/Postal Code"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>
                
                {/* Billing Address Section */}
                <div className="pt-2 border-t border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-300">
                      <CreditCard className="w-4 h-4 inline mr-1" />
                      Billing Address *
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-400">
                      <input
                        type="checkbox"
                        checked={billingSameAsMailing}
                        onChange={(e) => setBillingSameAsMailing(e.target.checked)}
                        className="rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                      />
                      Same as mailing
                    </label>
                  </div>
                  
                  {!billingSameAsMailing && (
                    <div className="space-y-3 mb-3">
                      <Input
                        type="text"
                        value={billingStreet}
                        onChange={(e) => setBillingStreet(e.target.value)}
                        placeholder="Billing Street Address"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="text"
                          value={billingCity}
                          onChange={(e) => setBillingCity(e.target.value)}
                          placeholder="City"
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        />
                        <Input
                          type="text"
                          value={billingState}
                          onChange={(e) => setBillingState(e.target.value)}
                          placeholder="State/Province"
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="text"
                          value={billingZip}
                          onChange={(e) => setBillingZip(e.target.value)}
                          placeholder="ZIP/Postal Code"
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        />
                        <Select value={billingCountry} onValueChange={setBillingCountry}>
                          <SelectTrigger className="bg-white/10 border-white/20 text-white">
                            <SelectValue placeholder="Country" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700 max-h-[200px]">
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c.code} value={c.code} className="text-white hover:bg-white/10">
                                <span className="flex items-center gap-2">
                                  <span>{c.flag}</span>
                                  <span>{c.name}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  
                  {/* Phone number - always required */}
                  <Input
                    type="tel"
                    value={billingPhone}
                    onChange={(e) => setBillingPhone(e.target.value)}
                    placeholder="Phone Number *"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
                
                {error && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}
                
                <Button
                  onClick={handleCompleteProfile}
                  disabled={loading || !country || !mailingStreet || !mailingCity || !mailingState || !mailingZip || !billingPhone || (!billingSameAsMailing && (!billingStreet || !billingCity || !billingState || !billingZip || !billingCountry))}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                >
                  {loading ? 'Saving...' : 'Complete Profile'}
                </Button>
              </div>
            )}

            {/* Waiting for Review Screen - shown when user has completed avatar but payment not yet approved */}
            {mode === 'waiting_review' && (
              <div className="text-center py-4 space-y-4">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center bg-blue-500/20">
                  <Clock className="w-10 h-10 text-blue-400" />
                </div>
                
                <h3 className="text-xl font-bold text-white">
                  Payment Under Review
                </h3>
                
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <p className="text-blue-300 text-sm">
                    Your payment proof is being reviewed. Approval may take up to 6 hours.
                  </p>
                  <p className="text-white font-medium mt-2">
                    Try to login after 30 minutes
                  </p>
                </div>
                
                {/* WhatsApp for faster review */}
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-gray-300 text-sm mb-3">Need faster account access?</p>
                  <a 
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Contact us on WhatsApp
                  </a>
                  <p className="text-gray-500 text-xs mt-2">
                    Send your payment confirmation for instant review
                  </p>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <p className="text-gray-400 text-xs mb-3">Account: {pendingPaymentEmail}</p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setMode('login');
                      setEmail(pendingPaymentEmail);
                    }}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Try Login Again
                  </Button>
                </div>
              </div>
            )}

            {/* Signup Success Screen */}
            {signupSuccess && signupData && mode !== 'complete_profile' && (
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-blue-500/20">
                  <CreditCard className="w-8 h-8 text-blue-400" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">
                  Account Created!
                </h3>
                
                <p className="text-gray-400 mb-4">
                  Please select a plan and submit payment proof to activate your account.
                </p>
                
                {signupData.referralInfo && (
                  <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                    <p className="text-green-400 text-sm">
                      🎉 Referral code saved! Will be applied to Premium plan.
                    </p>
                  </div>
                )}
                
                <div className="space-y-3">
                  <Button
                    onClick={() => setShowPaymentModal(true)}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Select Plan & Pay
                  </Button>
                  
                  <p className="text-xs text-gray-500">
                    Approval may take up to 6 hours, sometimes instantly.
                  </p>
                </div>
              </div>
            )}

            {/* Login Form - Step 1: Email only */}
            {mode === 'login' && !signupSuccess && (
              <form onSubmit={handleCheckEmail} className="space-y-4">
                {statusMessage ? (
                  <div className={`p-4 rounded-lg border ${
                    statusMessage.type === 'error' ? 'bg-red-500/10 border-red-500/30' :
                    statusMessage.type === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
                    statusMessage.type === 'success' ? 'bg-green-500/10 border-green-500/30' :
                    'bg-blue-500/10 border-blue-500/30'
                  }`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={
                        statusMessage.type === 'error' ? 'text-red-400' :
                        statusMessage.type === 'warning' ? 'text-yellow-400' :
                        statusMessage.type === 'success' ? 'text-green-400' : 'text-blue-400'
                      }>
                        {statusMessage.icon}
                      </div>
                      <h4 className={`font-medium ${
                        statusMessage.type === 'error' ? 'text-red-300' :
                        statusMessage.type === 'warning' ? 'text-yellow-300' :
                        statusMessage.type === 'success' ? 'text-green-300' : 'text-blue-300'
                      }`}>{statusMessage.title}</h4>
                    </div>
                    <p className="text-gray-400 text-sm">{statusMessage.message}</p>
                    
                    {/* WhatsApp link for faster review */}
                    {statusMessage.showWhatsApp && (
                      <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                        <p className="text-gray-300 text-xs mb-2">Need faster account access?</p>
                        <a 
                          href={WHATSAPP_LINK}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 font-medium"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Contact us on WhatsApp
                        </a>
                        <p className="text-gray-500 text-[10px] mt-1">
                          Send your payment confirmation for faster review
                        </p>
                      </div>
                    )}
                    
                    {statusMessage.showPayment && (
                      <Button
                        type="button"
                        onClick={() => setShowPaymentModal(true)}
                        className="mt-3 w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Submit Payment Proof
                      </Button>
                    )}
                    
                    {/* Subscription Renewal Section */}
                    {statusMessage.showRenewal && (
                      <div className="mt-4 space-y-3">
                        <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                          <p className="text-orange-300 text-sm font-medium mb-1">Renew Your Plan</p>
                          <p className="text-gray-400 text-xs">
                            Choose a plan for the <strong>BonBot Signal Generator</strong> to restore your access.
                          </p>
                        </div>
                        
                        <Button
                          type="button"
                          onClick={() => setShowPaymentModal(true)}
                          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Choose a Plan to Renew
                        </Button>
                        
                        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <p className="text-blue-300 text-xs text-center">
                            <strong>Note:</strong> After renewal, it may take up to <strong>30 minutes</strong> for access to be restored.
                          </p>
                        </div>
                        
                        {statusMessage.redirectUrl && (
                          <a
                            href={statusMessage.redirectUrl}
                            className="block w-full text-center py-2 px-4 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600 rounded-lg text-gray-300 text-sm transition-colors"
                          >
                            Visit forthewisepro.com →
                          </a>
                        )}
                        
                        <p className="text-gray-500 text-xs text-center">
                          Need help? Contact <a href="mailto:worood@forthewisepro.com" className="text-blue-400 hover:underline">support</a>
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    
                    {error && (
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                        {error}
                      </div>
                    )}
                    
                    <Button
                      type="submit"
                      disabled={loading || !email}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    >
                      {loading ? 'Checking...' : 'Continue'}
                    </Button>
                  </>
                )}
                
                <div className="text-center text-sm text-gray-400">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('signup'); setError(''); setStatusMessage(null); }}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    Sign up
                  </button>
                </div>
              </form>
            )}
            
            {/* Login Form - Step 2: Password entry */}
            {mode === 'login_password' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="text-center mb-4">
                  <p className="text-gray-400 text-sm">Logging in as</p>
                  <p className="text-white font-medium">{loginEmail}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
                
                {error && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}
                
                {statusMessage && (
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm">
                    {statusMessage.text}
                  </div>
                )}
                
                {/* reCAPTCHA Human Verification */}
                <div className="flex flex-col items-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => {
                      setCaptchaToken(token);
                      setCaptchaError('');
                    }}
                    onExpired={() => setCaptchaToken(null)}
                    theme="dark"
                  />
                  {captchaError && (
                    <p className="text-red-400 text-sm mt-2">{captchaError}</p>
                  )}
                </div>
                
                <Button
                  type="submit"
                  disabled={loading || !password || !captchaToken}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  {loading ? 'Signing in...' : 'Login'}
                </Button>
                
                <div className="flex justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setEmail(loginEmail); setError(''); setStatusMessage(null); }}
                    className="text-gray-400 hover:text-white"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={loading}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            )}
            
            {/* Reset Password Form */}
            {mode === 'reset_password' && (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-yellow-500/20">
                    <Mail className="w-8 h-8 text-yellow-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Reset Password</h3>
                  <p className="text-gray-400 text-sm">
                    Enter the code sent to<br />
                    <span className="text-white font-medium">{loginEmail}</span>
                  </p>
                </div>
                
                {statusMessage && (
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm">
                    {statusMessage.text}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Reset Code</label>
                  <Input
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 text-center text-2xl tracking-widest"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create new password (min 6 characters)"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
                
                {error && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}
                
                <Button
                  onClick={handleResetPassword}
                  disabled={loading || resetCode.length !== 6 || password.length < 6 || password !== confirmPassword}
                  className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
                
                <button
                  type="button"
                  onClick={() => { setMode('login_password'); setError(''); setStatusMessage(null); }}
                  className="w-full text-gray-400 hover:text-white text-sm"
                >
                  ← Back to login
                </button>
              </div>
            )}

            {/* Signup Form */}
            {mode === 'signup' && !signupSuccess && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Country *
                  </label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger className="w-full bg-white/10 border-white/20 text-white">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-[300px]">
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code} className="text-white hover:bg-white/10">
                          <span className="flex items-center gap-2">
                            <span>{c.flag}</span>
                            <span>{c.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Mailing Address Section */}
                <div className="pt-2 border-t border-white/10">
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Mailing Address *
                  </label>
                  <div className="space-y-3">
                    <Input
                      type="text"
                      value={mailingStreet}
                      onChange={(e) => setMailingStreet(e.target.value)}
                      placeholder="Street Address"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        type="text"
                        value={mailingCity}
                        onChange={(e) => setMailingCity(e.target.value)}
                        placeholder="City"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                      <Input
                        type="text"
                        value={mailingState}
                        onChange={(e) => setMailingState(e.target.value)}
                        placeholder="State/Province"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <Input
                      type="text"
                      value={mailingZip}
                      onChange={(e) => setMailingZip(e.target.value)}
                      placeholder="ZIP/Postal Code"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>
                
                {/* Referral Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Gift className="w-4 h-4 inline mr-1" />
                    Referral Code (optional)
                  </label>
                  <Input
                    type="text"
                    value={referralCode}
                    onChange={(e) => {
                      setReferralCode(e.target.value.toUpperCase());
                      if (e.target.value.length >= 4) {
                        validateReferralCode(e.target.value);
                      }
                    }}
                    placeholder="Enter code for discount"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                  {referralInfo && (
                    <p className={`text-xs mt-1 ${referralInfo.valid ? 'text-green-400' : 'text-gray-500'}`}>
                      {referralInfo.valid 
                        ? `✓ ${referralInfo.discount_percent}% discount from ${referralInfo.referrer_name}` 
                        : referralInfo.message}
                    </p>
                  )}
                </div>
                
                {error && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}
                
                <Button
                  type="button"
                  onClick={() => handleSignup()}
                  disabled={loading || !email || !name || !country || !mailingStreet || !mailingCity || !mailingState || !mailingZip}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
                
                <p className="text-xs text-gray-500 text-center">
                  You'll receive a verification code to your email
                </p>
                <p className="text-xs text-yellow-500 text-center flex items-center justify-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Please check your spam or junk folder
                </p>
                
                <div className="text-center text-sm text-gray-400">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); }}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    Login
                  </button>
                </div>
              </div>
            )}
            
            {/* Email Verification Screen */}
            {mode === 'verify_email' && (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-blue-500/20">
                  <Mail className="w-8 h-8 text-blue-400" />
                </div>
                
                <h3 className="text-xl font-bold text-white">Check Your Email</h3>
                <p className="text-gray-400 text-sm">
                  We sent a 6-digit verification code to<br />
                  <span className="text-white font-medium">{verificationEmail}</span>
                </p>
                
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-yellow-400 text-xs flex items-center justify-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Please check your spam or junk folder if you don't see the email
                  </p>
                </div>
                
                {statusMessage && (
                  <div className={`p-3 rounded-lg ${statusMessage.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-blue-500/10 border-blue-500/30 text-blue-400'} border text-sm`}>
                    {statusMessage.text}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Verification Code</label>
                  <Input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 text-center text-2xl tracking-widest"
                  />
                </div>
                
                {error && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}
                
                <Button
                  onClick={handleVerifyEmail}
                  disabled={loading || verificationCode.length !== 6}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                >
                  {loading ? 'Verifying...' : 'Verify Email'}
                </Button>
                
                <div className="text-sm text-gray-400">
                  Didn't receive the code?{' '}
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resendingCode}
                    className="text-blue-400 hover:text-blue-300 disabled:text-gray-500"
                  >
                    {resendingCode ? 'Sending...' : 'Resend Code'}
                  </button>
                </div>
              </div>
            )}
            
            {/* Password Setup Screen */}
            {mode === 'set_password' && (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-green-500/20">
                    <CheckCircle2 className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Almost Done!</h3>
                  <p className="text-gray-400 text-sm">
                    Create a password to secure your account
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password (min 6 characters)"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                  />
                </div>
                
                {error && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}
                
                <Button
                  onClick={handleSetPassword}
                  disabled={loading || password.length < 6 || password !== confirmPassword}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                >
                  {loading ? 'Setting Password...' : 'Complete Setup'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        userEmail={pendingPaymentEmail}
        isNewSignup={signupSuccess}
        onPaymentSubmitted={() => {
          setShowPaymentModal(false);
          // After payment proof submission, go directly to waiting for review page
          setSignupSuccess(false);
          setMode('waiting_review');
        }}
      />
    </>
  );
};

export default GuestAuthModal;
